import React, {useEffect, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {Box, Link, Typography} from '@mui/material';
import apiService from '../../services/apiService';
import {ROLES} from '../../config/roles';
import EditableCard from '../EditableCard';
import MachineCategorySelectForm from '../forms/MachineCategorySelectForm';

/**
 * "Category" card on the machine detail page. View = the assigned
 * category's image + name (linked to the category page). Edit = a Select
 * dropdown of all categories, persisted via PUT /api/machines/{id}/category.
 *
 * Categories are fetched once on mount (small payload, cached for the
 * card's lifetime). Form stays pure — list comes in as data via props.
 */
const MachineCategoryCard = ({machine, onUpdated}) => {
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        apiService.get('/api/categories')
            .then((data) => setCategories(Array.isArray(data) ? data : data?.content ?? []))
            .catch(() => setCategories([]));
    }, []);

    return (
        <EditableCard
            title="Category"
            protectedBy={MachineCategoryCard}
            protectAction="edit"
            value={{categoryId: machine.category?.id ?? null}}
            save={(draft) => apiService.put(`/api/machines/${machine.id}/category`, draft)}
            onSaved={onUpdated}
            successMessage="Category updated"
            renderView={() => <CategoryView category={machine.category}/>}
            renderForm={(draft, setDraft) => (
                <MachineCategorySelectForm value={draft} onChange={setDraft} categories={categories}/>
            )}
        />
    );
};

const CategoryView = ({category}) => {
    if (!category) return <Typography>—</Typography>;
    return (
        <Box sx={{textAlign: 'center'}}>
            <Link component={RouterLink} to={`/categories/${category.id}`} underline="none">
                {category.imageUrl && (
                    <Box component="img" src={category.imageUrl} alt={category.name}
                         sx={{width: '100%', maxWidth: 200, mb: 1, borderRadius: 1}}/>
                )}
                <Typography>{category.name}</Typography>
            </Link>
        </Box>
    );
};

MachineCategoryCard.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    edit: [ROLES.ADMINS],
};

export default MachineCategoryCard;
